import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import SideDrawer from '../../../../../../common/_custom/SideDrawer/SideDrawer';
import { COLORS } from '../../../../../../utils/constants/colors';
import styles from './DealTermsLoggingDrawer.module.scss';
import { CollapsableDropdown } from '../CollapsableDropdown/CollapsableDropdown';
import { Button } from '../../../../../../common/_custom';
import { ManageDealDrawerHeader } from '../../../../../../common/ManageDealDrawer/Components/ManageDealDrawerHeader';
import StatusDropdown from '../../../../../../common/_custom/StatusDropdown/StatusDropdown';
import { ClockTimer, plusWhite } from '../../../../../../assets/hostedassets';
import DealTermsLog from '../DealTermsLog/DealTermsLog';
import DeleteIcon from '../../../../../../assets/Svg/DeleteIcon';
import LoadingSpinner from '../../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import { useDealTermsLoggingDrawer } from './useDealTermsLoggingDrawer';
import ClockIcon from '../../../../../../assets/Svg/ClockIcon';
import { setConfigTableModal } from '../../../../../../store/investor/action';
import { useAppDispatch, useAppSelector } from '../../../../../../app/hooks';
import { ENTITY_TYPES } from '../../../../../../store/investor/constants';
import { isLenderType } from '../../../../../../utils/utils';
interface DealTermsLoggingDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    investorDealId: string;
    borrowerName: string;
    source: string;
    investorName: string;
    restrictBackwardMovement: boolean;
}

export type DealTermsLogType = {
    id?: string;
    tempId?: string;
    investorDealId: string;
    dealId: string;
    logStatus: string;
    logDate: string;
    changeDate: number;
    owner: {
        name: string;
        userId: string;
        activityBy: string;
        activityDate: string;
        ownerType: string;
    };
    dealTerms: DealTerms;
};

export type DealTerms = {
    logDate: string;
    changeDate: number;
    maximumDealAmount: number;
    minimumRoi: number;
    maximumRoi: number;
    minimumTenure?: number;
    maximumTenure: number;
    loanTypes: string[];
    securityOffered: string[];
    dealNotes: Array<{
        note: string;
        noteBy: string;
        noteDate: string;
    }>;
    documents: Array<{
        docId: number;
        documentName: string;
        action: string;
    }>;
};

export type DealTermsLogs = {
    dealTermsLogs: Array<DealTermsLogType>;
};

const DealTermsLoggingDrawer: React.FC<DealTermsLoggingDrawerProps> = ({
    isOpen,
    onClose,
    investorDealId,
    borrowerName,
    source,
    investorName,
    restrictBackwardMovement,
}) => {
    const dispatch = useAppDispatch();
    const userType = useAppSelector((state) => state.user.userData.accountType);
    const isLender = isLenderType(userType);
    const {
        // States
        dealTermsLoggingConfig,
        dealTermsLogs,
        runtimeLogs,
        selectedStatus,
        hasChanges,
        expandedLogStatus,
        isLoading,
        isInitialLoading,
        latestLogRef,

        // Constants
        primaryCtaText,
        secondaryCtaText,
        headerCtsText,
        dropdownOptions,

        // Methods
        setIsFormValid,
        handleStatusChange,
        handleLogUpdate,
        handleDeleteRuntimeLog,
        handleDropdownToggle,
        handleClose,
        handleSave,
        sourceBasedActions,
        handleCancel,
        getStatusDropdownOption,
        deletingLogIds,
        handleDeleteWithAnimation,
        highestStatusIndexReached,
    } = useDealTermsLoggingDrawer({
        isOpen,
        onClose,
        investorDealId,
    });

    // Helper function to calculate time elapsed in days only
    const calculateTimeElapsed = (date: string, nextDate: string): string => {
        const logDate = dayjs(date);
        const nextLogDate = nextDate ? dayjs(nextDate) : dayjs();
        const duration = dayjs.duration(nextLogDate.diff(logDate));

        if (duration.asDays() >= 1) {
            return Math.floor(duration.asDays()) > 1 ? `${Math.floor(duration.asDays())} days` : `${Math.floor(duration.asDays())} day`;
        } else if (duration.asHours() >= 1) {
            return Math.floor(duration.asHours()) > 1
                ? `${Math.floor(duration.asHours())} hours`
                : `${Math.floor(duration.asHours())} hour`;
        } else if (duration.asMinutes() >= 1) {
            return Math.floor(duration.asMinutes()) > 1
                ? `${Math.floor(duration.asMinutes())} minutes`
                : `${Math.floor(duration.asMinutes())} minute`;
        } else if (duration.asSeconds() >= 1) {
            return Math.floor(duration.asSeconds()) > 1
                ? `${Math.floor(duration.asSeconds())} seconds`
                : `${Math.floor(duration.asSeconds())} second`;
        } else {
            return 'just now';
        }
    };

    const formattedTime = (date: string): string => {
        return dayjs(date).format('h:mm a, D MMM YYYY');
    };

    useEffect(() => {
        if (dealTermsLogs?.dealTermsLogs?.length > 0) {
            sourceBasedActions(source);
        }
    }, [isInitialLoading]);

    if (!isOpen) return null;

    // Create log position map for date constraints
    const createLogPositionMap = () => {
        const allLogs = [...(dealTermsLogs?.dealTermsLogs || []), ...runtimeLogs];
        const logPositionMap = new Map<string, { logDate: string; logPosition: number }>();

        allLogs.forEach((log, index) => {
            const logId = log?.id || log?.tempId;
            if (logId) {
                logPositionMap.set(logId, {
                    logDate: log.logDate,
                    logPosition: index + 1, // 1-based position
                });
            }
        });

        return logPositionMap;
    };

    const handleSaveClick = () => {
        if (isLenderType(userType)) {
            handleSave();
            return;
        }

        dispatch(
            setConfigTableModal({
                show: true,
                data: {
                    entity: ENTITY_TYPES.DEAL,
                    actionType: 'updateDealTermsLogs',
                    dropdownOption: selectedStatus?.value,
                    rowData: null, // Modal will fetch deal data via API using dealId
                    source: [ENTITY_TYPES.DEAL],
                    dealId: investorDealId,
                    onSuccess: () => {
                        handleSave();
                    },
                },
            }),
        );
    };

    const logPositionMap = createLogPositionMap();

    const renderCollapsedContent = (content, isRuntimeLog: boolean, isExpanded: boolean, onDelete?: () => void) => (
        <div className={styles.triggerContent}>
            <div className={styles.leftContent}>
                <div
                    style={{
                        backgroundColor: content.status?.backgroundColor || 'var(--secondary-bg-colour)',
                        color: content.status?.labelColor || 'var(--primary-text-colour)',
                        padding: '6px 12px',
                        borderRadius: '44px',
                        fontSize: '14px',
                        fontWeight: 500,
                    }}
                >
                    {content.status?.label === 'First Deal Shared' ? 'Deal Shared' : content.status?.label}
                </div>
                <div className={styles.timeElapsed}>
                    <ClockIcon width="14" height="14" />
                    <span>{calculateTimeElapsed(content.date, content.nextDate)}</span>
                </div>
            </div>
            {!(isExpanded && isRuntimeLog) && (
                <div className={styles.rightContent}>
                    <img src={ClockTimer} alt="clock" />
                    {content.date && content.by && (
                        <span className={styles.dateBy}>
                            {formattedTime(content.date)} by {content.by}
                        </span>
                    )}
                    {content.amount && <span className={styles.amount}>{content.amount} Cr</span>}
                </div>
            )}
            {isExpanded && isRuntimeLog && (
                <div
                    className={styles.deleteIcon}
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete?.();
                    }}
                >
                    <DeleteIcon width={16} height={16} className={styles.deleteIcon} />
                </div>
            )}
        </div>
    );

    const AddNewButton = (
        <Button
            text={headerCtsText}
            onClick={() => {}}
            variant="purple"
            icon={plusWhite}
            size="small"
            style={{ height: '40px', marginTop: '0px', width: '137px' }}
            iconStyles={{ marginRight: '10px' }}
        />
    );

    return (
        <SideDrawer
            onClose={handleClose}
            showHeading={false}
            style={{
                backgroundColor: COLORS.LIGHT_GREY_BG,

                height: '100%',
            }}
            iconStyle={{ zIndex: 20 }}
            width={'50%'}
        >
            <div className={styles.DrawerHeader}>
                <ManageDealDrawerHeader
                    drawerLabel={'Deal Terms'}
                    investorName={
                        <div className={styles.Label}>
                            <div className={styles.sublabel}>{borrowerName}</div>
                            <div className={styles.sublabel}>{`< > ${investorName}`}</div>
                        </div>
                    }
                    headerActionStyle={{ marginRight: '3rem' }}
                    headerAction={
                        <StatusDropdown
                            dropdownOptions={dropdownOptions}
                            selectedOption={selectedStatus}
                            restrictBackwardMovement={restrictBackwardMovement}
                            selectOption={handleStatusChange}
                            customTrigger={AddNewButton}
                            showClearButton={false}
                            field="dealStatus"
                            customSelectedOptionIndex={highestStatusIndexReached}
                            disabled={!isLender}
                        />
                    }
                />
            </div>
            <div className={styles.DrawerContent}>
                {isInitialLoading ? (
                    <div className={styles.loaderContainer}>
                        <LoadingSpinner height="44px" color={COLORS.PRIMARY_PURPLE} />
                    </div>
                ) : (
                    dealTermsLogs?.dealTermsLogs &&
                    [...dealTermsLogs?.dealTermsLogs, ...runtimeLogs].map((entry: DealTermsLogType, index, array) => {
                        const isRuntimeLog = !entry?.id;
                        const logIdentifier = entry?.id || entry?.tempId;
                        const isLastLog = index === array?.length - 1;
                        const isDeleting = deletingLogIds.has(logIdentifier || '');
                        const statusOption = getStatusDropdownOption(entry?.logStatus);

                        return (
                            <div
                                key={logIdentifier}
                                className={`${styles.DropdownContainer} ${isDeleting ? styles.deleting : ''}`}
                                ref={isLastLog ? latestLogRef : null}
                            >
                                <CollapsableDropdown
                                    triggerContent={(isExpanded) =>
                                        renderCollapsedContent(
                                            {
                                                date: entry?.changeDate,
                                                nextDate: array[index + 1]?.changeDate,
                                                by: entry?.owner?.name,
                                                amount: entry?.dealTerms?.maximumDealAmount,
                                                status: statusOption,
                                            },
                                            isRuntimeLog,
                                            isExpanded,
                                            isRuntimeLog ? () => handleDeleteWithAnimation(entry?.tempId!) : undefined,
                                        )
                                    }
                                    className="custom-dropdown"
                                    isExpanded={expandedLogStatus === logIdentifier}
                                    onToggle={(isExpanded) => handleDropdownToggle(entry, isExpanded)}
                                >
                                    <DealTermsLog
                                        formData={{
                                            ...entry,
                                            currentLogId: logIdentifier, // Add current log identifier
                                        }}
                                        dealTermsLoggingConfig={dealTermsLoggingConfig}
                                        setIsFormValid={setIsFormValid}
                                        onUpdateLog={handleLogUpdate}
                                        logPositionMap={logPositionMap}
                                    />
                                </CollapsableDropdown>
                            </div>
                        );
                    })
                )}
            </div>
            <div className={`${styles.DrawerFooter} ${hasChanges ? styles.visible : ''}`}>
                <div className={styles.ButtonContainer}>
                    <Button
                        text={secondaryCtaText}
                        onClick={handleCancel}
                        variant="secondary"
                        disabled={isLoading}
                        style={{ marginTop: '0px' }}
                    />
                    <Button
                        text={primaryCtaText}
                        onClick={handleSaveClick}
                        variant="purple"
                        disabled={isLoading}
                        loading={isLoading}
                        style={{ marginTop: '0px' }}
                    />
                </div>
            </div>
        </SideDrawer>
    );
};

export default DealTermsLoggingDrawer;
